import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, LayoutGrid, Receipt, MapPin, Boxes, Search, X } from "lucide-react";

const AppCtx = createContext(null);
export const useApps = () => useContext(AppCtx) || { active: null, activeModules: null, apps: [] };
const ICONS = { receipt: Receipt, "map-pin": MapPin, boxes: Boxes, "layout-grid": LayoutGrid };

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [activeSlug, setActiveSlug] = useState(localStorage.getItem("td_active_app") || "sales");

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/apps");
      setApps(data.apps);
      if (!data.apps.find((a) => a.slug === activeSlug) && data.apps[0]) setActiveSlug(data.apps[0].slug);
    } catch { /* not fatal */ }
  }, [activeSlug]);
  useEffect(() => { if (user) reload(); }, [user]);  // eslint-disable-line

  const setActive = (slug) => { setActiveSlug(slug); localStorage.setItem("td_active_app", slug); };
  const active = apps.find((a) => a.slug === activeSlug) || apps[0] || null;
  const value = { apps, active, activeModules: active ? active.modules : null, setActive, reload };
  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

function NineDots({ light = false }) {
  return (
    <span className="grid grid-cols-3 gap-[3px]" aria-hidden>
      {Array.from({ length: 9 }).map((_, i) => <span key={i} className={`w-[5px] h-[5px] rounded-[1px] ${light ? "bg-white" : "bg-[#F83A84]"}`} />)}
    </span>
  );
}

// Vertical, switchable app list — used inside the launcher popovers and the mobile drawer.
export function AppList({ q = "", onPick }) {
  const { apps, active, setActive } = useApps();
  const filtered = apps.filter((a) => a.name.toLowerCase().includes(q.trim().toLowerCase()));
  if (filtered.length === 0) return <div className="px-2 py-3 text-sm text-[#6B7189]">No apps found</div>;
  return (
    <div className="space-y-0.5" data-testid="app-list">
      {filtered.map((a) => {
        const Icon = ICONS[a.icon] || LayoutGrid;
        const on = active?.slug === a.slug;
        return (
          <button key={a.slug} onClick={() => { setActive(a.slug); onPick?.(); }} data-testid={`app-opt-${a.slug}`}
            className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm ${on ? "bg-[#101D51]/8 text-[#101D51] font-semibold" : "text-[#101D51] hover:bg-[#F5F6FA]"}`}>
            <span className="w-7 h-7 rounded-md bg-[#F83A84]/10 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#F83A84]" /></span>
            <span className="truncate">{a.name}</span>
            {on && <Check className="w-4 h-4 ml-auto text-[#0B7C5A] shrink-0" />}
          </button>);
      })}
    </div>
  );
}

function LauncherPanel({ dropUp = false, align = "left", onClose }) {
  const [q, setQ] = useState("");
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className={`absolute ${align === "right" ? "right-0" : "left-0"} w-72 bg-white rounded-xl border border-[#E6E8F0] shadow-2xl z-40 p-3 ${dropUp ? "bottom-full mb-2" : "mt-2 top-full"}`} data-testid="app-switcher-menu">
        <div className="flex items-center justify-between mb-2">
          <span className="text-base font-bold text-[#101D51]">App Launcher</span>
          <button onClick={onClose} className="text-[#6B7189] hover:text-[#101D51]" data-testid="app-launcher-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6B7189]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search apps…" data-testid="app-launcher-search"
            className="w-full h-9 pl-8 pr-2 rounded-lg border border-[#E6E8F0] text-sm focus:outline-none focus:border-[#101D51]" />
        </div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-[#6B7189] px-1 mb-1">Apps</div>
        <div className="max-h-64 overflow-y-auto"><AppList q={q} onPick={onClose} /></div>
      </div>
    </>
  );
}

// Desktop tab-bar switcher: 9-dots + active app name.
export function AppSwitcher({ dropUp = false }) {
  const { active } = useApps();
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);
  return (
    <div className="relative flex items-center gap-2.5">
      <button onClick={toggle} data-testid="app-switcher-btn" title="App Launcher"
        className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-[#E9EAF1] transition-colors shrink-0">
        <NineDots />
      </button>
      <button onClick={toggle} className="text-base font-bold text-[#101D51] truncate max-w-[200px] text-left" data-testid="active-app-name">
        {active?.name || "Sales"}
      </button>
      {open && <LauncherPanel dropUp={dropUp} onClose={() => setOpen(false)} />}
    </div>);
}

// Mobile floating 9-dot launcher (bottom-right); menu drops up and right-aligned.
export function AppLauncherButton() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="app-launcher-fab" title="App Launcher"
        className="w-12 h-12 rounded-full bg-[#101D51] shadow-lg flex items-center justify-center active:scale-95 transition-transform">
        <NineDots light />
      </button>
      {open && <LauncherPanel dropUp align="right" onClose={() => setOpen(false)} />}
    </div>);
}
